import React from 'react';
import { Container, Dialog, Typography, Button } from '@mui/material';
import { EventValidationError } from '../../../model/event-validation-error';
import ErrorStatusLine from '../../commons/ErrorStatusLine';
import EventCompletionFooter from '../../commons/EventCompletionFooter';
import EventCompletionInfoHeader from '../../commons/EventCompletionInfoHeader';
import EventCompletionInfoSubHeader from '../../commons/EventCompletionInfoSubHeader';
import EventCompletionContent from '../../commons/EventCompletionContent';
import EventCompletionSubContent from '../../commons/EventCompletionSubContent';
import { useEffect, useState } from 'react';
import SuccessStatusLine from '../../commons/SuccessStatusLine';
import { EventDay } from '../../../model/event-day';
import { OverrideReasonPayload } from '../../../model/override-reason-payload';

interface IProps {
  open: boolean;
  onClose: (backToHome: boolean) => void;
  eventDates: string;
  eventId: string;
  userName: string;
  validationErrors: EventValidationError[];
  pictureDays: EventDay[];
  overrideReasons?: OverrideReasonPayload[];
  completionMessage?: string;
}

function EventCompletionModal({
  open,
  onClose,
  eventDates,
  eventId,
  userName,
  validationErrors,
  pictureDays,
  overrideReasons,
  completionMessage,
}: IProps) {
  const [successStatuses, setSuccessStatuses] = useState<string[]>([]);
  const handleClose = (backToHome: boolean = false) => onClose(backToHome);

  useEffect(() => {
    const successes = [];
    const hasError = (containText: string) =>
      !!validationErrors?.find(
        (validationError) =>
          validationError.errorMessage.indexOf(containText) > 0
      );

    // check for expected days
    if (!hasError('expected date')) {
      if (pictureDays.length === 1) {
        successes.push('1 of 1 day complete!');
      } else {
        successes.push(
          `${pictureDays.length} of ${pictureDays.length} days complete!`
        );
      }
    }

    // check for cameras
    if (!hasError('camera')) {
      let cameraCount = 0;

      pictureDays.forEach(
        (pictureDay) => (cameraCount += pictureDay.cameras?.length || 0)
      );

      if (cameraCount === 1) {
        successes.push(`1 of 1 expected camera is complete!`);
      } else {
        successes.push(
          `${cameraCount} of ${cameraCount} expected cameras are complete!`
        );
      }
    }

    if (
      successes.length !== successStatuses.length ||
      !successes.every((success, idx) => success === successStatuses[idx])
    ) {
      setSuccessStatuses(successes);
    }
  }, [pictureDays, validationErrors, successStatuses]);

  return (
    <Dialog
      open={open}
      fullWidth={true}
      maxWidth='md'
      onClose={() => handleClose(false)}
    >
      <EventCompletionInfoHeader title='Success!' onClose={handleClose} />
      <Container>
        <Typography sx={{ mt: 2 }} variant='h6' color='primary.dark'>
          {completionMessage}
        </Typography>
        <EventCompletionInfoSubHeader
          eventDates={eventDates}
          eventId={eventId}
          userName={userName}
        />

        <EventCompletionContent>
          <EventCompletionSubContent title='Event Status:'>
            {successStatuses.map((successMessage) => (
              <SuccessStatusLine
                key={successMessage}
                message={successMessage}
              />
            ))}
            {validationErrors?.map((err) => (
              <ErrorStatusLine
                key={err.errorMessage}
                message={err.errorMessage}
              />
            ))}
          </EventCompletionSubContent>

          {overrideReasons && (
            <EventCompletionSubContent title='Override Reason(s):'>
              {overrideReasons.map((overrideReason) => (
                <React.Fragment key={overrideReason.reasonId}>
                  <SuccessStatusLine
                    message={
                      overrideReason.description +
                      (overrideReason.requiresNote ? ':' : '')
                    }
                  />

                  {overrideReason.requiresNote && (
                    <Typography
                      variant='h4'
                      sx={{ ml: 4, fontStyle: 'italic', fontWeight: 'normal' }}
                    >
                      {overrideReason.note}
                    </Typography>
                  )}
                </React.Fragment>
              ))}
            </EventCompletionSubContent>
          )}
        </EventCompletionContent>

        <EventCompletionFooter>
          <Button
            variant='contained'
            sx={{ mb: 3, width: { xs: '100%', sm: '45%' } }}
            onClick={() => handleClose(true)}
          >
            Exit & Return to Home
          </Button>

          <Button
            variant='outlined'
            sx={{ mb: 3, width: { xs: '100%', sm: '45%' } }}
            onClick={() => handleClose(false)}
          >
            Back to Event Details
          </Button>
        </EventCompletionFooter>
      </Container>
    </Dialog>
  );
}

export default EventCompletionModal;
